import React from "react";

const RELOAD_KEY = "dmv_chunk_reload_at";

/** True for errors caused by a stale bundle after a new deploy (old chunk files no longer exist). */
export function isChunkLoadError(err) {
  const msg = String(err?.message || err || "");
  return err?.name === "ChunkLoadError" || /Loading (CSS )?chunk [\w-]+ failed/i.test(msg) || /Unexpected token '?<'?/.test(msg);
}

/** Reload once (max every 5 min) to pick up the newly deployed bundle. */
export function reloadForNewVersion() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < 300000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {}
  window.location.reload();
  return true;
}

/**
 * Catches render errors so the app shows a friendly screen instead of a blank page.
 * - `inline` renders a compact panel inside the page (used around each route, so the
 *   header and footer stay usable) and resets automatically when the route changes.
 * - The technical message is shown under "Details" so it can be reported.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, open: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Page crashed:", error, info?.componentStack);
    if (isChunkLoadError(error)) reloadForNewVersion();
  }

  componentDidUpdate(prev) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null, open: false });
  }

  render() {
    if (!this.state.error) return this.props.children;
    const msg = String(this.state.error?.message || this.state.error);
    const inline = this.props.inline;
    return (
      <div style={{ minHeight: inline ? "60vh" : "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: inline ? "transparent" : "#FBF7F4", padding: 24, fontFamily: "Jost, system-ui, sans-serif" }} role="alert">
        <div style={{ maxWidth: 460, textAlign: "center", background: "#fff", border: "1px solid #E7DDD3", borderRadius: 20, padding: "32px 26px", boxShadow: "0 20px 50px rgba(77,14,18,.10)" }}>
          <img src="/logo-mark.png" alt="" style={{ width: 54, height: 54, objectFit: "contain", margin: "0 auto 10px", display: "block" }} onError={(e) => { e.currentTarget.style.display = "none"; }}/>
          <h1 style={{ color: "#8C1C22", fontSize: 24, margin: "0 0 6px", fontFamily: "'Marcellus', serif", fontWeight: 400 }}>This page didn't load properly</h1>
          <p style={{ color: "#5C3B3D", fontSize: 15, lineHeight: 1.55, margin: "0 0 18px" }}>Reload to try again. If it keeps happening, send us the details below.</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => window.location.reload()} style={{ background: "#8C1C22", color: "#FBF7F4", border: 0, borderRadius: 999, padding: "10px 22px", fontWeight: 600, cursor: "pointer", fontSize: 15 }}>Reload page</button>
            <button onClick={() => { window.location.href = "/"; }} style={{ background: "transparent", color: "#8C1C22", border: "1.5px solid #8C1C22", borderRadius: 999, padding: "10px 22px", fontWeight: 600, cursor: "pointer", fontSize: 15 }}>Go to home</button>
          </div>
          <button onClick={() => this.setState({ open: !this.state.open })} style={{ marginTop: 16, background: "none", border: 0, color: "#8a6f70", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>{this.state.open ? "Hide details" : "Details"}</button>
          {this.state.open && <pre style={{ textAlign: "left", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12, background: "#FBF7F4", borderRadius: 10, padding: 10, color: "#5C3B3D", marginTop: 8 }}>{msg}{"\n"}{window.location.pathname}</pre>}
        </div>
      </div>
    );
  }
}
